/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {AuthService, getTokenKey, TokenRefresh} from '@exc/auth';
import Emitter from '@exc/emitter';
import metrics, {Level} from '@adobe/exc-app/metrics';
import {storage} from '@exc/storage';

const TEN_HOURS = 1000 * 60 * 60 * 10;

interface CachedToken extends TokenRefresh {
  dateStored: number;
}

type TokenCache = Record<string, CachedToken>;

export default class CustomTokenService extends Emitter {
  private readonly metrics = metrics.create('exc.core.services.CustomTokenService');
  private readonly tokenPromises: Record<string, Promise<TokenRefresh>> = {};

  /**
   * Gets token for re-auth from cache or IMS.
   * @param clientId Client id string.
   * @param scope List of scopes.
   * @param auth - AuthService instance.
   * @returns Profile, token.
   */
  public async getToken(clientId: string, scope: string, auth: AuthService): Promise<TokenRefresh> {
    let userId = auth.getUserId();
    if (!userId) {
      this.metrics.warn('getUserId returned null');
      const profile = await auth.fetchUserProfile();
      userId = profile?.userId;
      if (!userId) {
        this.metrics.error('Failed to get User ID from token and profile in custom token service');
      }
    }

    const key = getTokenKey(clientId, scope, userId);
    const cachedInfo = await this.getCachedToken(key);

    // If the cached token is available, return it.
    if (cachedInfo) {
      const {token} = cachedInfo;
      const {imsData, success} = await auth.validateTokenAndRefresh({clientId, scope, token, userId});
      if (!success) {
        throw new Error(`Unable to validate and refresh token for client: ${clientId}`);
      }
      const finalToken = imsData || cachedInfo;
      this.updateCachedTokens(key, finalToken);
      auth.trackToken(finalToken.token, {clientId, scope, userId});
      return finalToken;
    }

    // Prevent duplicate calls by storing promises.
    if (key in this.tokenPromises) {
      return this.tokenPromises[key];
    }

    // Create a promise and store it to prevent redundant calls.
    const tokenPromise = this.tokenPromises[key] = this.getTokenFromIMS(clientId, scope, userId, auth);

    // Need to asynchronously fetch the token from IMS for the custom client id.
    // Upon completion, sends the payload if one is queued.
    const newIms = await tokenPromise;
    newIms && this.updateCachedTokens(key, newIms);

    delete this.tokenPromises[key];
    return newIms;
  }

  /**
   * Gets token from IMS.
   * @param clientId Client id string.
   * @param scope List of scopes.
   * @param userId User id string.
   * @param auth AuthService instance.
   * @returns Profile, token.
   */
  private async getTokenFromIMS(clientId: string, scope: string, userId: string, auth: AuthService): Promise<TokenRefresh> {
    try {
      const customTokenTimer = this.metrics.start('CustomToken', {clientId});
      const res = await auth.fetchCustomImsAccessToken(clientId, scope, userId);
      customTokenTimer.time('done', {clientId, success: (res && !!res.access_token)});
      const {access_token, ...profile} = res;
      return {profile, token: access_token};
    } catch (e: any) {
      let error = e;
      if (e && e.error && e.jump) {
        this.metrics.event(
          'Core.tokenRideError',
          `${clientId} token error: ${e.error}`,
          {level: Level.ERROR}
        );
        error = new Error(e.error);
        this.emit('rideError', e);
      } else {
        this.metrics.error(`Failed to fetch token for client id: ${clientId}`, e);
      }
      throw error;
    }
  }

  /**
   * Sets and updates cached token for sandbox re-auth. Stores token with
   * current date using clientId and scopes.
   * @param key - Cached Token key
   * @param tokenData Ims object with profile and token info.
   */
  public async updateCachedTokens(key: string, tokenData?: TokenRefresh) {
    const cachedTokens = await storage.session.get<TokenCache>('shellCachedTokens') || {};
    if (!tokenData) {
      delete cachedTokens[key];
    } else {
      const {profile, token} = tokenData;
      cachedTokens[key] = {dateStored: Date.now(), profile, token};
    }
    storage.session.set('shellCachedTokens', cachedTokens, TEN_HOURS);
  }

  /**
   * Gets cached token for sandbox re-auth. Should retrieve from cache if
   * switching solutions within 10 minutes.
   * @returns Profile, token, and date info.
   */
  private getCachedToken = async (key: string): Promise<CachedToken | undefined> => {
    const cachedTokens = await storage.session.get<TokenCache>('shellCachedTokens') || {};

    if (!(key in cachedTokens)) {
      return;
    }

    const info = cachedTokens[key];
    const dateDiff = Date.now() - info.dateStored;
    if (dateDiff < TEN_HOURS) {
      this.updateCachedTokens(key, info);
      return info;
    }
    // clear expired token by passing no token
    this.updateCachedTokens(key);
  };
}
